import type {
  BomOperationPayload,
  BomVersion,
  BomVersionOperation,
  BomVersionPayload,
  TechnicalPassportMaterial,
  TechnicalPassportRoute,
} from './types'

const STAGE_ALIASES: Array<[RegExp, string]> = [
  [/knit|to'q|вяз/i, 'knitting'],
  [/rosso|toe.?clos|мыс/i, 'rosso'],
  [/board|form|пар/i, 'boarding'],
  [/label|yorliq|этик/i, 'labeling'],
  [/pack|qadoq|упаков/i, 'packaging'],
]

/** Legacy single-word stage alias of an operation (`knitting`, `rosso`, ...). */
export function legacyStageCodeForOperation(operation: BomVersionOperation): string {
  const name = operation.operation_name.trim()
  return STAGE_ALIASES.find(([pattern]) => pattern.test(name))?.[1] ?? 'finishing'
}

/**
 * One stage code per BOM operation, so the passport stage list mirrors the product's
 * BOM instead of collapsing every unrecognised operation into `finishing`.
 *
 * `op_<sequence>_<slug>` is the free-form stage-code shape the backend already accepts
 * (`TechnicalPassportRules::isValidStageCode`, 2-50 chars, `[a-z0-9._-]`).
 */
export function stageCodeForOperation(operation: BomVersionOperation): string {
  const slug = operation.operation_name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40) || 'stage'
  const sequence = Number(operation.operation_sequence) || 0
  return `op_${sequence}_${slug}`
}

export function bomToPassportRoutes(bom: BomVersion): TechnicalPassportRoute[] {
  return bom.operations.map((operation) => ({
    operation_id: operation.operation_reference_id ?? undefined,
    operation_name: operation.operation_name,
    department_agent_id: operation.department_agent_id ?? undefined,
    workstation_agent_id: operation.equipment_reference_id ?? undefined,
    workstation_name: operation.equipment_name ?? undefined,
    standard_time_minutes: operation.duration_minutes,
    cost_per_produced_unit_day: operation.cost_per_produced_unit_day,
    cost_per_produced_unit_night: operation.cost_per_produced_unit_night,
    qc_required: false,
    sort_order: operation.operation_sequence,
  }))
}

export function bomToPassportMaterials(
  bom: BomVersion,
): TechnicalPassportMaterial[] {
  return bom.operations.flatMap((operation) =>
    operation.materials.map((material) => ({
      stage_code: stageCodeForOperation(operation),
      material_product_id: material.material_id,
      material_name_snapshot: material.material_name,
      consumption_per_pair: material.quantity,
      waste_percent: material.loss_percentage,
      uom: material.material_unit,
      sort_order: operation.operation_sequence,
    })),
  )
}

export function applyBomToPassport(
  bom: BomVersion,
): { routes: TechnicalPassportRoute[]; materials: TechnicalPassportMaterial[] } {
  return {
    routes: bomToPassportRoutes(bom),
    materials: bomToPassportMaterials(bom),
  }
}

/** BOM operation that owns a passport stage code (current code or a legacy alias). */
export function operationForStageCode(
  bom: BomVersion,
  stageCode?: string | null,
): BomVersionOperation | null {
  const code = (stageCode ?? '').trim().toLowerCase()
  if (!code) return null
  return (
    bom.operations.find((operation) => stageCodeForOperation(operation).toLowerCase() === code)
    ?? bom.operations.find((operation) => legacyStageCodeForOperation(operation) === code)
    ?? null
  )
}

/** A material is the same row when it sits on the same stage with the same product. */
function materialKey(row: TechnicalPassportMaterial): string {
  return `${(row.stage_code ?? '').trim().toLowerCase()}|${row.material_product_id ?? ''}`
}

/**
 * Union of the passport's raw materials and the BOM materials. The passport rows
 * win (they carry the extra colour / thread-code fields); BOM-only materials are
 * appended at their stage so the passport always mirrors the BOM.
 */
export function mergeBomMaterialsIntoPassport(
  bom: BomVersion,
  materials: TechnicalPassportMaterial[],
): TechnicalPassportMaterial[] {
  const merged = materials.map((row) => ({ ...row }))
  const seen = new Set(merged.map(materialKey))
  for (const row of bomToPassportMaterials(bom)) {
    const key = materialKey(row)
    if (seen.has(key)) continue
    seen.add(key)
    merged.push({ ...row, sort_order: merged.length + 1 })
  }
  return merged
}

export interface BomMaterialSyncResult {
  /** BOM payload with the passport's materials folded in — send with `PUT /bom/{id}`. */
  payload: BomVersionPayload
  added: number
  updated: number
  /** Passport rows whose stage code does not match any BOM operation. */
  unmatched: number
}

/**
 * Folds the passport's raw materials back into the BOM operations of their stage:
 * missing materials are appended to that operation, existing ones take the
 * passport's consumption / waste values. Rows without a material product are skipped.
 */
export function applyPassportMaterialsToBom(
  bom: BomVersion,
  materials: TechnicalPassportMaterial[],
): BomMaterialSyncResult {
  const operations: BomOperationPayload[] = bom.operations.map((operation) => ({
    operation_sequence: operation.operation_sequence,
    operation_reference_id: operation.operation_reference_id ?? undefined,
    operation_name: operation.operation_name,
    department_agent_id: operation.department_agent_id ?? null,
    equipment_reference_id: operation.equipment_reference_id ?? undefined,
    equipment_name: operation.equipment_name ?? undefined,
    duration_minutes: operation.duration_minutes,
    cost_per_produced_unit_day: operation.cost_per_produced_unit_day,
    cost_per_produced_unit_night: operation.cost_per_produced_unit_night,
    materials: operation.materials.map((material) => ({
      material_id: material.material_id,
      quantity: material.quantity,
      loss_percentage: material.loss_percentage,
    })),
  }))

  let added = 0
  let updated = 0
  let unmatched = 0

  for (const row of materials) {
    const materialId = Number(row.material_product_id ?? 0)
    if (!Number.isInteger(materialId) || materialId <= 0) continue

    const operation = operationForStageCode(bom, row.stage_code)
    const index = operation ? bom.operations.indexOf(operation) : -1
    if (index < 0) {
      unmatched += 1
      continue
    }

    const quantity = Math.max(0, Number(row.consumption_per_pair ?? 0))
    const loss = Math.max(0, Number(row.waste_percent ?? 0))
    const target = operations[index]
    const existing = target.materials.find((material) => material.material_id === materialId)

    if (!existing) {
      target.materials.push({ material_id: materialId, quantity, loss_percentage: loss })
      added += 1
      continue
    }
    if (Math.abs(existing.quantity - quantity) > 1e-9 || Math.abs(existing.loss_percentage - loss) > 1e-9) {
      existing.quantity = quantity
      existing.loss_percentage = loss
      updated += 1
    }
  }

  return {
    added,
    updated,
    unmatched,
    payload: {
      product_id: bom.product_id,
      description: bom.description ?? undefined,
      operations,
    },
  }
}
